// apps/client/src/config/leadTabAccess.ts
//
// ONE access map for the Lead Capture subapp, used by BOTH the top nav and
// the per-route guard.
//
// Why this exists
// ---------------
// Nav visibility and route access had drifted apart from what the callables
// actually enforce, in both directions:
//
//   - LeadCaptureTopNav's ADMIN_TIER_ROLES included `office_admin`, so an
//     office admin saw Setup Wizard, Integrations, Taxonomy, Routing Rules,
//     Pipeline, CRM Mappings, Lead Sources and Cost Savings. Every one of
//     those callables rejects office_admin
//     (leadCaptureAction's adminOnlyActions enforces
//     ['super_admin','administrator','marketing']), so the tabs opened onto a
//     permission error.
//
//   - It also included `founder`, which does not exist in the client app at
//     all (CLAUDE.md section 7: founder is admin-portal only and is signed
//     out of the client).
//
//   - AppRoutes put every /leads/* config route under ONE RequireRole that
//     admits the whole broker family and service_staff, so the admin-only
//     tabs were hidden but not actually guarded: typing the URL reached the
//     page.
//
// The fix is a single source of truth. Each entry records the roles the
// PAGE'S OWN CALLABLES accept, read from those callables rather than
// inferred. Nothing here widens a callable; this map can only be equal to or
// narrower than the server.
//
// Keep in step with the server
// ----------------------------
// If a callable's enforceRole list changes, change it here too. The
// rbac-surface audit baseline is what catches a server-side change; this
// file is the client mirror of it.

import type { AppRole } from '@valutten/shared-utils/roles';

/**
 * Roles that exist in the CLIENT app. A subset of the shared AppRole union:
 * `founder` is admin-portal only (CLAUDE.md section 7) and `loyalty_client`
 * is a different app entirely, so neither can appear here. Typing against
 * AppRole keeps this compatible with useCapability's `legacyAllow`.
 */
export type LeadTabRole = Exclude<AppRole, 'founder' | 'loyalty_client'>;

/** Slugs a broker-family user can be granted individually via claims. */
export type LeadPageSlug =
  | 'how_it_works'
  | 'attribution'
  | 'email_automation'
  | 'templates';

export interface LeadTabAccess {
  /** Roles always allowed, mirroring the page's callables. */
  roles: readonly LeadTabRole[];
  /**
   * When set, a broker-family user ALSO gets the tab if
   * `claims.allowedLeadPages` contains this slug. This is the existing
   * per-user grant, preserved.
   */
  brokerPageSlug?: LeadPageSlug;
  /**
   * When set, access is decided by this capability instead of `roles`
   * (with `roles` as the legacy fallback list). Only the DM inbox uses it.
   */
  capability?: string;
}

/** Everyone who can reach the Lead Capture subapp at all. */
const ALL_LEAD_ROLES: readonly LeadTabRole[] = [
  'super_admin',
  'administrator',
  'office_admin',
  'credit_analyst',
  'broker',
  'marketing',
  'broker_assistant',
  'service_staff',
];

/**
 * leadCaptureAction `adminOnlyActions` -> enforceRole(['super_admin',
 * 'administrator','marketing']). This is the big one office_admin was
 * wrongly shown.
 */
const LEAD_CONFIG_ROLES: readonly LeadTabRole[] = [
  'super_admin',
  'administrator',
  'marketing',
];

/** emailSequenceAction ADMIN_OR_MARKETING_ROLES (office_admin IS included). */
const EMAIL_ROLES: readonly LeadTabRole[] = [
  'super_admin',
  'administrator',
  'office_admin',
  'marketing',
];

export const LEAD_TAB_ACCESS: Record<string, LeadTabAccess> = {
  // Informational only, no callable. Broker family can be granted it.
  '/leads/how-it-works': {
    roles: LEAD_CONFIG_ROLES,
    brokerPageSlug: 'how_it_works',
  },
  // leadCaptureAction generateSourceToken / generatePerOfficeToken.
  '/leads/sources': { roles: LEAD_CONFIG_ROLES },
  // leadAction listLeads admits every lead role.
  '/leads/inbox': { roles: ALL_LEAD_ROLES },
  // sendOutboundMessage / conversationAction. credit_analyst excluded.
  '/leads/dm-inbox': {
    capability: 'access_conversations_inbox',
    roles: [
      'super_admin',
      'administrator',
      'office_admin',
      'broker',
      'broker_assistant',
      'marketing',
    ],
  },
  // getLeadAttribution enforceRole.
  '/leads/attribution': {
    roles: LEAD_CONFIG_ROLES,
    brokerPageSlug: 'attribution',
  },
  // getMarketingSourceReport enforceRole.
  '/leads/monthly-report': { roles: LEAD_CONFIG_ROLES },
  // emailSequenceAction.
  '/leads/email': { roles: EMAIL_ROLES, brokerPageSlug: 'email_automation' },
  '/leads/templates': { roles: EMAIL_ROLES, brokerPageSlug: 'templates' },
  // edmCampaignAction enforceRole(['super_admin','administrator','marketing']); office_admin excluded.
  // The tab is ALSO hidden unless featureFlags.edm_broadcast === true (nav
  // edmEnabled prop + RequireEdmEnabled); the flag is not an input here so
  // this function keeps its role-only signature. /leads/edm/import is a
  // sub-route under this entry, not a tab, with its own company-wide guard.
  '/leads/edm': { roles: LEAD_CONFIG_ROLES },
  // websiteConnectionAction: super_admin writes; marketing read-only (list/get/health).
  '/leads/website': { roles: ['super_admin', 'marketing'] },
  // consentTextAction enforceRole(['super_admin']).
  '/leads/consent': { roles: ['super_admin'] },
  // consentAction ROLES ['super_admin','administrator','marketing'].
  '/leads/unsubscribes': { roles: LEAD_CONFIG_ROLES },
  // leadCaptureAction getOnboardingState / markStepComplete / saveBranding.
  '/leads/setup': { roles: LEAD_CONFIG_ROLES },
  '/leads/integrations': { roles: LEAD_CONFIG_ROLES },
  // leadCaptureAction listLeadStaff / saveLeadStaff -> ['super_admin','marketing'].
  '/leads/staff': { roles: ['super_admin', 'marketing'] },
  '/leads/taxonomy': { roles: LEAD_CONFIG_ROLES },
  '/leads/routing': { roles: LEAD_CONFIG_ROLES },
  // getUnifiedPipeline enforceRole.
  '/leads/pipeline': { roles: LEAD_CONFIG_ROLES },
  // listGrowthSignals / decideGrowthSignal enforceRole(['super_admin',
  // 'administrator','marketing']). The tab is ALSO hidden unless
  // featureFlags.growth === 'propose' (nav growthProposeEnabled prop +
  // RequireGrowthPropose, which also checks the lead_capture add-on).
  '/leads/growth': { roles: LEAD_CONFIG_ROLES },
  '/leads/crm-mappings': { roles: LEAD_CONFIG_ROLES },
  // saveBrokerAvailability is a self-service write for every role.
  '/leads/availability': { roles: ALL_LEAD_ROLES },
  // Reads onboarding state via leadCaptureAction (admin-only actions).
  '/leads/cost-savings': { roles: LEAD_CONFIG_ROLES },
  // getLeadSlaSummary enforceRole (office_admin IS included, and the query
  // is office-scoped).
  '/leads/sla': {
    roles: ['super_admin', 'administrator', 'office_admin', 'marketing'],
  },
};

const BROKER_FAMILY_ROLES = new Set<string>([
  'broker',
  'broker_assistant',
  'credit_analyst',
]);

/**
 * Roles that may reach the Lead Capture subapp at all. Anything outside this
 * set is denied before any per-tab rule is considered, so a capability grant
 * can never let `founder` (admin portal only) or `loyalty_client` (a
 * different app) in through the DM inbox's capability branch.
 */
const CLIENT_LEAD_ROLES = new Set<string>(ALL_LEAD_ROLES);

/**
 * Whether `role` may reach `path`.
 *
 * `hasCapability` is supplied by the caller so this module stays free of
 * React hooks and can be used from both the nav and a route guard.
 */
export function canAccessLeadTab(
  path: string,
  role: string | undefined,
  allowedLeadPages: ReadonlySet<string> | null,
  hasCapability?: (capability: string, legacyAllow: readonly string[]) => boolean,
): boolean {
  const entry = LEAD_TAB_ACCESS[path];
  // Unknown path: fail closed rather than assume it is public.
  if (!entry) return false;
  if (!role) return false;
  // Role must be one the client app actually has. This runs BEFORE the
  // capability branch: otherwise a capability grant would admit `founder`,
  // who must never gain a client-app capability.
  if (!CLIENT_LEAD_ROLES.has(role)) return false;

  if (entry.capability && hasCapability) {
    // Capability AND the tab's own role list: the capability narrows, it
    // does not replace the role boundary.
    return (
      hasCapability(entry.capability, entry.roles) &&
      (entry.roles as readonly string[]).includes(role)
    );
  }

  if ((entry.roles as readonly string[]).includes(role)) return true;

  // Per-user broker grant.
  if (
    entry.brokerPageSlug &&
    BROKER_FAMILY_ROLES.has(role) &&
    allowedLeadPages?.has(entry.brokerPageSlug)
  ) {
    return true;
  }

  return false;
}

/** Tab paths in nav order. */
export const LEAD_TAB_ORDER: readonly string[] = [
  '/leads/how-it-works',
  '/leads/sources',
  '/leads/inbox',
  '/leads/dm-inbox',
  '/leads/attribution',
  '/leads/monthly-report',
  '/leads/email',
  '/leads/templates',
  '/leads/edm',
  '/leads/website',
  '/leads/consent',
  '/leads/unsubscribes',
  '/leads/setup',
  '/leads/integrations',
  '/leads/staff',
  '/leads/taxonomy',
  '/leads/routing',
  '/leads/pipeline',
  '/leads/growth',
  '/leads/crm-mappings',
  '/leads/sla',
  '/leads/availability',
  '/leads/cost-savings',
];

/**
 * The single Growth inbox flag gate: strictly `featureFlags.growth ===
 * 'propose'` ('collect' collects without surfacing; absent means off). Used
 * by the top nav and by RequireGrowthPropose so the two cannot disagree; the
 * callables re-check it server-side.
 */
export const isGrowthProposeEnabled = (f?: Record<string, unknown> | null): boolean =>
  f?.growth === 'propose';
